import React, { useReducer, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import InitialValuesForm from '@/constants/initialValues';
import { Navbar } from '@packages/ui/layout';
import { useConfirm } from '@packages/ui/composites';
import { NotificationModal } from '@packages/shared/notification';
import { ReactComponent as Logo } from '@/assets/svg/LogoHeader.svg';
import { selectToken } from '@/pages/auth/authSlice';
import { selectAuthRoutesList } from '@/pages/configuration/routeSlice';
import AuthService from '@/services/authService';
import useWealthlaneCustomerApi from '@/hooks/useWealthlaneCustomerApi';
import useGetCustomerUserDetails from '@/hooks/get/useGetCustomerUserDetails';
import { useGetCompanyProfileDetails } from '@/hooks';

import { useBrandedLogo } from '@apps/customer/hooks/useBrandedLogo';
import { useNavMenu } from '@apps/customer/hooks/useNavMenu';
import { useTenantInfo } from '@apps/customer/hooks/useTenantInfo';
import { useNotifications } from '@apps/customer/hooks/useNotifications';
import { useProfileMenu } from '@apps/customer/hooks/useProfileMenu';
import { useCompanyDisplay } from '@apps/customer/hooks/useCompanyDisplay';
import useOnline from '@/hooks/custom-hooks/useOnline';

import NavbarSwitchModal from '@/pages/navbar-modal';
import { Banner } from '@packages/ui/layout/Banner';
import { NavMenuRenderer } from './NavMenuRenderer';
import { ProfileMenuRenderer } from './ProfileMenuRenderer';
import { MobileMenuRenderer } from './MobileMenuRenderer';
import { navbarUIReducer, initialNavbarUIState } from './store/navbarUIReducer';

const CustomerNavbar: React.FC = () => {
  const token = useSelector(selectToken);
  const userAuthList = useSelector(selectAuthRoutesList);
  const { isOffline } = useOnline();

  const [ui, dispatch] = useReducer(navbarUIReducer, initialNavbarUIState);

  const { handleRequest: handleCustomerRequest } = useWealthlaneCustomerApi();
  const { logoUrl, showLogo, homeLink } = useBrandedLogo();
  const tenantInfo = useTenantInfo({ handleCustomerRequest });
  const menuGroups = useNavMenu({ tenantType: tenantInfo.tenantType });

  const closeNotifModal = useCallback(() => dispatch({ type: 'NOTIFICATION_CLOSE' }), []);

  const {
    count: notificationCount,
    notifications,
    notificationsLoading,
    deletingId,
    onDelete,
    onClearAll,
    onItemClick,
    formatDate,
  } = useNotifications({ token, onCloseModal: closeNotifModal });

  const openSwitchModal = useCallback(() => dispatch({ type: 'SWITCH_MODAL_OPEN' }), []);

  const profileMenuItems = useProfileMenu({
    canSwitch: tenantInfo.canSwitch,
    onSwitchInstance: openSwitchModal,
  });

  const isAuth = Boolean(token);
  const { data: userName } = useGetCustomerUserDetails(handleCustomerRequest, isAuth);
  const { data: companyDetails, isLoading: isCompanyLoading } = useGetCompanyProfileDetails(
    handleCustomerRequest,
    isAuth
  );

  const companyDisplay = useCompanyDisplay({ handleCustomerRequest, companyDetails });
  const { confirm } = useConfirm();

  const handleGoHome = async () => {
    const { confirmed } = await confirm({
      title: `You are about to change to ${
        companyDisplay.homeCompanyName ?? ''
      } Company, Main instance. Are you sure?`,
      semanticId: 'goHomeConfirm',
    });
    if (confirmed) companyDisplay.onGoHome();
  };

  const fullName = userName?.firstName
    ? `${userName.firstName ?? ''} ${userName.lastName ?? ''}`
    : '';

  return (
    <>
      <Navbar.Shell
        semanticId='navbarContainer'
        variant={tenantInfo.variant}
        position='fixed'
        mobileOpen={ui.mobileMenuOpen}
        onMobileOpenChange={(open) =>
          dispatch({ type: open ? 'MOBILE_MENU_OPEN' : 'MOBILE_MENU_CLOSE' })
        }
        overlay={
          <NotificationModal
            isOpen={ui.notificationModalOpen}
            onClose={closeNotifModal}
            notifications={notifications}
            loading={notificationsLoading}
            deletingId={deletingId}
            onItemClick={onItemClick}
            onDelete={onDelete}
            onClearAll={onClearAll}
            formatDate={formatDate}
            id='notificationModal'
          />
        }>
        {showLogo && (
          <Navbar.Brand semanticId='navBrand'>
            <Link
              to={homeLink}
              className='flex items-center justify-center space-x-4'
              id='navLogoLink'>
              {logoUrl ? (
                <img
                  className='nav-brand'
                  id='navLogo'
                  src={logoUrl}
                  alt='App Logo'
                />
              ) : (
                <Logo className='nav-brand' id='navLogo' />
              )}
            </Link>
          </Navbar.Brand>
        )}

        {Boolean(token) && (
          <>
            <Navbar.MobileToggle semanticId='mobileMenuToggle' />

            <NavMenuRenderer groups={menuGroups} />

            <Navbar.Actions semanticId='navActions'>
              <Navbar.NotificationBell
                count={notificationCount}
                active={ui.notificationModalOpen}
                onClick={() => dispatch({ type: 'NOTIFICATION_OPEN' })}
                semanticId='notificationBell'
              />

              <Navbar.Spacer width={5} />

              {!isCompanyLoading && !companyDisplay.isLoading && (
                <Navbar.InstanceSwitch
                  id='desktopNavLogo'
                  logoUrl={companyDisplay.logoUrl}
                  companyName={companyDisplay.companyName}
                  instanceName={tenantInfo.instanceName ?? undefined}
                  switchable={tenantInfo.canSwitch}
                  isSwitched={tenantInfo.isSwitched}
                  onClick={() => tenantInfo.canSwitch && dispatch({ type: 'SWITCH_MODAL_OPEN' })}
                  onGoHome={handleGoHome}
                  semanticId='instanceSwitch'
                />
              )}

              <Navbar.Spacer width={5} />

              <ProfileMenuRenderer items={profileMenuItems} userName={fullName} />
            </Navbar.Actions>

            <MobileMenuRenderer
              groups={menuGroups}
              userSection={{
                sectionLabel: userAuthList?.currentUser?.name,
                children: (
                  <>
                    {!isCompanyLoading && !companyDisplay.isLoading && (
                      <Navbar.InstanceSwitch
                        logoUrl={companyDisplay.logoUrl}
                        companyName={companyDisplay.companyName}
                        instanceName={tenantInfo.instanceName ?? undefined}
                        switchable={tenantInfo.canSwitch}
                        isSwitched={tenantInfo.isSwitched}
                        onClick={() =>
                          tenantInfo.canSwitch &&
                          dispatch({ type: 'SWITCH_MODAL_OPEN_FROM_MOBILE' })
                        }
                        onGoHome={handleGoHome}
                        semanticId='mobileInstanceSwitch'
                      />
                    )}
                    <Navbar.MobileNavItem semanticId='mobileLogout'>
                      <span
                        className='cursor-pointer'
                        onClick={() => void new AuthService().logout()}>
                        Log Out
                      </span>
                    </Navbar.MobileNavItem>
                  </>
                ),
              }}
            />
          </>
        )}
      </Navbar.Shell>

      {isOffline && (
        <Banner
          variant="warning"
          position="fixed"
          icon="wifi_off"
          semanticId="networkNotice"
        >
          We are experiencing transient network issues. Certain features may be temporarily limited,
          but your current view and data are preserved.
        </Banner>
      )}

      {ui.switchModalOpen && (
        <NavbarSwitchModal
          isModalOpen={ui.switchModalOpen}
          closeModal={() => dispatch({ type: 'SWITCH_MODAL_CLOSE' })}
          modalStatus={'Add'}
          initialData={{ data: InitialValuesForm.switchSandboxModal, id: '' }}
          setModalStatus={() => 'Add'}
          domId='navbarSwitchModal'
        />
      )}
    </>
  );
};

export default CustomerNavbar;
